import { markAppStale } from './appVersionStore';

export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const INDEX_DOCUMENT_URL = '/index.html';
const ENTRY_SCRIPT_PATH_PATTERN = /\/assets\/index-[^"'\s?#]+\.js/;
const SCRIPT_TAG_PATTERN = /<script\b[^>]*>/gi;
const MODULE_TYPE_PATTERN = /\btype\s*=\s*["']module["']/i;
const SRC_ATTRIBUTE_PATTERN = /\bsrc\s*=\s*["']([^"']+)["']/i;
const ENTRY_SCRIPT_SELECTOR = 'script[type="module"][src*="/assets/index-"]';

function toEntryScriptPath(source: string | null | undefined): string | null {
    return source?.match(ENTRY_SCRIPT_PATH_PATTERN)?.[0] ?? null;
}

/** 從 index.html 文字取出入口 script（/assets/index-<hash>.js）；找不到回傳 null */
export function extractEntryScriptPath(html: string): string | null {
    for (const scriptTag of html.match(SCRIPT_TAG_PATTERN) ?? []) {
        if (!MODULE_TYPE_PATTERN.test(scriptTag)) continue;
        const entryPath = toEntryScriptPath(scriptTag.match(SRC_ATTRIBUTE_PATTERN)?.[1]);
        if (entryPath) return entryPath;
    }
    return null;
}

/** 這個分頁當初是由哪支入口 script 載入的 */
export function readLoadedEntryScriptPath(doc: Document): string | null {
    return toEntryScriptPath(doc.querySelector(ENTRY_SCRIPT_SELECTOR)?.getAttribute('src'));
}

/** 任一邊讀不到（例如部署中途回了錯誤頁）都當作「無法判斷」，不要誤報 */
export function isNewVersionDeployed(
    loadedEntryPath: string | null,
    latestEntryPath: string | null
): boolean {
    return (
        loadedEntryPath !== null && latestEntryPath !== null && loadedEntryPath !== latestEntryPath
    );
}

interface PollingOptions {
    doc?: Document;
    fetchImpl?: typeof fetch;
    intervalMs?: number;
    onNewVersion?: () => void;
    isDevelopment?: boolean;
}

/**
 * 分頁回到前景、取得焦點，以及每隔一段時間（分頁隱藏時暫停）就向伺服器要一次 index.html，
 * 比對入口 script 的雜湊。請求失敗一律安靜略過，下一輪再試。發現新版後停止輪詢。
 */
export function startNewVersionPolling(options: PollingOptions = {}): () => void {
    const {
        doc = document,
        fetchImpl = (input, init) => fetch(input, init),
        intervalMs = VERSION_CHECK_INTERVAL_MS,
        onNewVersion = markAppStale,
        isDevelopment = import.meta.env.DEV,
    } = options;

    const loadedEntryPath = readLoadedEntryScriptPath(doc);
    if (isDevelopment || loadedEntryPath === null) return () => undefined;

    let inFlight: AbortController | null = null;
    let intervalHandle: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
        inFlight?.abort();
        if (intervalHandle !== null) clearInterval(intervalHandle);
        doc.removeEventListener('visibilitychange', checkWhenVisible);
        window.removeEventListener('focus', checkNow);
    };

    async function checkNow(): Promise<void> {
        if (doc.hidden || inFlight) return;
        const controller = new AbortController();
        inFlight = controller;
        try {
            const response = await fetchImpl(INDEX_DOCUMENT_URL, {
                cache: 'no-store',
                signal: controller.signal,
            });
            if (!response.ok) return;
            const latestEntryPath = extractEntryScriptPath(await response.text());
            if (isNewVersionDeployed(loadedEntryPath, latestEntryPath)) {
                stop();
                onNewVersion();
            }
        } catch {
            // 離線或部署中途連不上都是暫時的；下一輪再檢查，不打擾使用者
        } finally {
            if (inFlight === controller) inFlight = null;
        }
    }

    function checkWhenVisible(): void {
        if (!doc.hidden) void checkNow();
    }

    intervalHandle = setInterval(() => void checkNow(), intervalMs);
    doc.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkNow);
    return stop;
}
